import { Box, Grid, Paper, useMediaQuery, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";

import { renderAutocompleteField } from "./fields/renderAutocompleteField";
import { renderDateField } from "./fields/renderDateField";
import { renderSelectField } from "./fields/renderSelectField";
import { renderSwitchField } from "./fields/renderSwitchField";
import { renderTextField } from "./fields/renderTextField";

export type TFilterFieldsLocal = Record<string, any>;

export type ToolbarFieldConfig = {
	key: string;
	label: string;
	type: "autocomplete" | "select" | "date" | "text" | "switch" | "custom";
	show?: boolean | ((filters: TFilterFieldsLocal) => boolean);
	options?: any[];
	defaultValue?: any;
	onChange?: (value: any, filters: TFilterFieldsLocal, setFilter: SetFilterFn) => void;
	renderCustom?: (filters: TFilterFieldsLocal, setFilter: SetFilterFn) => React.ReactNode;
	extraProps?: Record<string, any>;
	group?: string;
};

type SetFilterFn = (key: string, value: any) => void;

type Props = {
	fieldsConfig: ToolbarFieldConfig[];
	filterInitialParams?: TFilterFieldsLocal;
	onSearch?: (filters: TFilterFieldsLocal) => void;
	onClear?: () => void;
};

export const ToolbarSinisterGeneric = ({ fieldsConfig, filterInitialParams = {}, onSearch, onClear }: Props) => {
	const [filters, setFilters] = useState<TFilterFieldsLocal>(filterInitialParams);
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));

	const setFilter: SetFilterFn = useCallback((key, value) => {
		let sanitized: any;
		if (typeof value === "boolean") {
			sanitized = value ? 1 : 0;
		} else if (Array.isArray(value)) {
			sanitized = value.map((v) => (typeof v === "string" ? v.trim() : v));
		} else {
			sanitized = value;
		}
		setFilters((prev) => ({ ...prev, [key]: sanitized }));
	}, []);

	const handleSearch = useCallback(() => {
		onSearch?.({ ...filters, forceReload: Date.now().toString() });
	}, [filters, onSearch]);

	const handleClear = (key: string) => {
		setFilter(key, "");
		if (key === "textSearch") onClear?.();
	};

	const getLabelFromValue = (value: string, field?: ToolbarFieldConfig): string => {
		if (!field?.options) return value;
		const found = field.options.find((opt) => String(opt?.value) === String(value));
		return found?.label ?? value;
	};

	const groupedFields = useMemo(() => {
		return fieldsConfig.reduce((acc, field) => {
			const group = field.group ?? "__solo__";
			if (!acc[group]) acc[group] = [];
			acc[group].push(field);
			return acc;
		}, {} as Record<string, ToolbarFieldConfig[]>);
	}, [fieldsConfig]);

	const renderField = (field: ToolbarFieldConfig) => {
		const shouldShow = typeof field.show === "function" ? field.show(filters) : field.show ?? true;
		if (!shouldShow) return null;

		if (field.type === "custom" && field.renderCustom) {
			return (
				<Grid item key={field.key}>
					{field.renderCustom(filters, setFilter)}
				</Grid>
			);
		}

		switch (field.type) {
			case "select":
				return renderSelectField({ field, filters, setFilter, getLabelFromValue });
			case "switch":
				return renderSwitchField({ field, filters, setFilter });
			case "text":
				return renderTextField({ field, filters, setFilter, handleSearch, handleClear });
			case "date":
				return renderDateField({ field, filters, setFilter });
			case "autocomplete":
				return renderAutocompleteField({ field, filters, setFilter });
			default:
				return null;
		}
	};

	return (
		<Box component={Paper} display="flex" flexDirection={smDown ? "column" : "row"} alignItems="center" px={2} py={1} mx={1} gap={1}>
			<Grid container direction={smDown ? "column" : "row"} gap={1} alignItems="flex-end" wrap="wrap">
				{Object.entries(groupedFields).map(([group, fields]) =>
					group === "__solo__" ? (
						fields.map(renderField)
					) : (
						<Grid item key={group}>
							<Grid container spacing={2} alignItems="flex-end" wrap="nowrap">
								{fields.map(renderField)}
							</Grid>
						</Grid>
					)
				)}
			</Grid>
		</Box>
	);
};
